import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Coins, Loader2, MessageCircle, Share2, Trophy } from "lucide-react";
import { plyxApi as api } from "@/lib/plyxApi";
import { usePlyxAuth } from "@/context/PlyxAuthContext";
import { toast } from "sonner";

/**
 * PlyxMillionsBattle — `/plyx/millions/battle/:battleId`
 * Shareable team-battle lobby: join a side, watch the roster fill,
 * play your climb when it goes live, and see the settled result.
 */

const QUICK_EMOJI = ["🔥", "😂", "💀", "🏆"];

const mergeMsgs = (prev, fresh) => {
  const seen = new Set(prev.map((m) => m.msg_id));
  return [...prev, ...fresh.filter((m) => !seen.has(m.msg_id))].slice(-100);
};

const BattleChat = ({ battleId, user }) => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const listRef = useRef(null);
  const lastTsRef = useRef("");

  useEffect(() => {
    let mounted = true;
    const poll = () => {
      const after = lastTsRef.current;
      api.get(`/plyx/millions/battle/${battleId}/chat${after ? `?after=${encodeURIComponent(after)}` : ""}`)
        .then((r) => {
          if (!mounted) return;
          const fresh = r.data?.messages || [];
          if (fresh.length) {
            lastTsRef.current = fresh[fresh.length - 1].created_at;
            setMessages((m) => mergeMsgs(m, fresh));
          }
        })
        .catch(() => {});
    };
    poll();
    const t = setInterval(poll, 3500);
    return () => { mounted = false; clearInterval(t); };
  }, [battleId]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages.length]);

  const send = async (quick) => {
    const body = (quick ?? text).trim();
    if (!body || sending) return;
    if (!user) { navigate("/plyx/login"); return; }
    setSending(true);
    try {
      const r = await api.post(`/plyx/millions/battle/${battleId}/chat`, { text: body });
      const m = r.data?.message;
      if (m) setMessages((arr) => mergeMsgs(arr, [m]));
      if (!quick) setText("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Message failed");
    } finally { setSending(false); }
  };

  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-3 mt-2" data-testid="battle-chat">
      <div className="text-sm font-bold mb-2 flex items-center gap-1.5">
        <MessageCircle className="w-4 h-4 text-[#FF2D7A]" /> Battle chat
      </div>
      <div ref={listRef} className="max-h-52 overflow-y-auto space-y-1.5 mb-2 pr-1" data-testid="battle-chat-messages">
        {messages.length === 0 ? (
          <p className="text-xs text-white/35 py-3 text-center">No messages yet — start the trash talk 🔥</p>
        ) : messages.map((m) => (
          <div key={m.msg_id} className="text-sm leading-snug" data-testid={`battle-chat-msg-${m.msg_id}`}>
            <span className={`font-extrabold mr-1.5 ${m.side === "a" ? "text-[#FFB42D]" : m.side === "b" ? "text-[#00E0C7]" : "text-white/50"}`}>
              @{m.handle}
            </span>
            <span className="text-white/85 break-words">{m.text}</span>
          </div>
        ))}
      </div>
      <div className="flex gap-1.5 mb-2">
        {QUICK_EMOJI.map((e, i) => (
          <button key={e} type="button" onClick={() => send(e)} disabled={sending}
            data-testid={`battle-chat-quick-${i}`}
            className="px-2.5 py-1 rounded-full bg-white/10 hover:bg-white/20 text-sm disabled:opacity-50">{e}</button>
        ))}
      </div>
      {user ? (
        <div className="flex gap-2">
          <input value={text} maxLength={280}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") send(); }}
            placeholder="Say something…" data-testid="battle-chat-input"
            className="flex-1 min-w-0 px-3 py-2 rounded-full bg-white/[0.06] border border-white/10 text-sm outline-none focus:border-[#FF2D7A]" />
          <button type="button" onClick={() => send()} disabled={sending || !text.trim()} data-testid="battle-chat-send"
            className="px-4 py-2 rounded-full bg-[#FF2D7A] text-white text-sm font-extrabold disabled:opacity-40 shrink-0">Send</button>
        </div>
      ) : (
        <Link to="/plyx/login" className="block text-center text-xs text-white/50 underline py-1" data-testid="battle-chat-login">
          Log in to chat
        </Link>
      )}
    </div>
  );
};

const TeamColumn = ({ team, size, side, canJoin, joining, onJoin, rungs, winner }) => (
  <div
    className={`flex-1 rounded-2xl border p-3 ${winner ? "border-[#FFB42D] bg-[#FFB42D]/10" : "border-white/10 bg-white/[0.04]"}`}
    data-testid={`battle-team-${side}`}
  >
    <div className="font-extrabold text-sm mb-2 flex items-center gap-1.5 truncate">
      {winner && <Trophy className="w-4 h-4 text-[#FFB42D] shrink-0" />}
      {team.name}
    </div>
    <div className="space-y-1.5">
      {team.members.map((m) => (
        <div key={m.plyx_user_id} className="flex items-center gap-2 text-sm">
          {m.avatar_url ? (
            <img src={m.avatar_url} alt="" className="w-6 h-6 rounded-full object-cover" />
          ) : (
            <div className="w-6 h-6 rounded-full bg-white/10 grid place-items-center text-[10px] font-bold">
              {(m.handle || "?")[0]?.toUpperCase()}
            </div>
          )}
          <span className="font-semibold truncate flex-1">@{m.handle}</span>
          {rungs && rungs[m.plyx_user_id] != null && (
            <span className="text-xs text-[#00E0C7] font-bold shrink-0">score {rungs[m.plyx_user_id]}</span>
          )}
        </div>
      ))}
      {Array.from({ length: Math.max(0, size - team.members.length) }).map((_, i) => (
        <div key={`slot-${i}`} className="flex items-center gap-2 text-sm text-white/30">
          <div className="w-6 h-6 rounded-full border border-dashed border-white/20" /> Open spot
        </div>
      ))}
    </div>
    {canJoin && team.members.length < size && (
      <button
        type="button"
        disabled={joining}
        onClick={() => onJoin(side)}
        data-testid={`battle-join-${side}`}
        className="w-full mt-3 py-2 rounded-full bg-[#00E0C7] text-black text-xs font-extrabold disabled:opacity-50"
      >
        {joining ? "Joining…" : "Join this team"}
      </button>
    )}
  </div>
);

export default function PlyxMillionsBattle() {
  const { battleId } = useParams();
  const { user } = usePlyxAuth();
  const navigate = useNavigate();
  const [battle, setBattle] = useState(null);
  const [missing, setMissing] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/plyx/millions/battle/${battleId}`)
      .then((r) => setBattle(r.data?.battle))
      .catch(() => setMissing(true));
  }, [battleId]);

  useEffect(() => {
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [load]);

  const join = async (side) => {
    if (!user) { navigate("/plyx/login"); return; }
    setBusy(true);
    try {
      await api.post(`/plyx/millions/battle/${battleId}/join`, { side });
      toast.success("You're in — stake escrowed!");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not join");
    } finally { setBusy(false); }
  };

  const act = async (action) => {
    setBusy(true);
    try {
      await api.post(`/plyx/millions/battle/${battleId}/${action}`);
      toast.success(action === "cancel" ? "Battle cancelled — stakes refunded" : "You left — stake refunded");
      if (action === "leave") navigate("/plyx/millions"); else load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally { setBusy(false); }
  };

  const share = () => {
    const url = `${window.location.origin}/plyx/millions/battle/${battleId}`;
    if (navigator.share) navigator.share({ title: "Join my Plyx Millions team battle!", url }).catch(() => {});
    else { navigator.clipboard?.writeText(url); toast.success("Battle link copied — send it to your squad!"); }
  };

  if (missing) {
    return (
      <div className="min-h-screen millions-stage text-white flex flex-col items-center justify-center gap-3 px-6" data-testid="millions-battle-missing">
        <div className="text-4xl">⚔️</div>
        <p className="font-bold">Battle not found</p>
        <Link to="/plyx/millions" className="text-sm text-[#FFB42D] underline">Back to Plyx Millions</Link>
      </div>
    );
  }
  if (!battle) {
    return (
      <div className="min-h-screen millions-stage text-white flex items-center justify-center" data-testid="millions-battle-loading">
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading battle…
      </div>
    );
  }

  const pid = user?.plyx_user_id;
  const isMember = !!battle.my_side;
  const isCreator = pid && battle.creator_id === pid;
  const canJoin = battle.status === "open" && !isMember;
  const canPlay = battle.status === "active" && isMember && (!battle.my_session || battle.my_session.status === "active");
  const iWon = battle.status === "settled" && battle.winner_side && battle.my_side === battle.winner_side;
  const statusPill = {
    open: { label: "Open — waiting for players", cls: "bg-[#00E0C7]/15 text-[#00E0C7]" },
    active: { label: "LIVE — climbs in progress", cls: "bg-[#FFB42D]/15 text-[#FFB42D]" },
    settled: { label: "Settled", cls: "bg-white/10 text-white/70" },
    cancelled: { label: "Cancelled", cls: "bg-white/10 text-white/50" },
  }[battle.status] || { label: battle.status, cls: "bg-white/10 text-white/70" };

  return (
    <div className="min-h-screen millions-stage text-white pb-24" data-testid="millions-battle-page">
      <div className="max-w-2xl mx-auto px-4 pt-6">
        <div className="flex items-center justify-between mb-4">
          <Link to="/plyx/millions" className="flex items-center gap-1 text-white/60 text-sm" data-testid="battle-back">
            <ArrowLeft className="w-4 h-4" /> Millions hub
          </Link>
          <button type="button" onClick={share} data-testid="battle-share"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 text-xs font-bold">
            <Share2 className="w-3.5 h-3.5" /> Share join link
          </button>
        </div>

        <h1 className="text-2xl font-extrabold">
          {battle.team_a.name} <span className="text-white/40">vs</span> {battle.team_b.name}
        </h1>
        <div className="flex items-center gap-2 mt-2 mb-4 flex-wrap text-xs font-bold">
          <span className={`px-2.5 py-1 rounded-full ${statusPill.cls}`} data-testid="battle-status">{statusPill.label}</span>
          <span className="px-2.5 py-1 rounded-full bg-white/10 inline-flex items-center gap-1">
            <Coins className="w-3 h-3 text-[#FFB42D]" /> {battle.stake_coins} coins each
          </span>
          <span className="px-2.5 py-1 rounded-full bg-white/10">{battle.category_label}</span>
          <span className="px-2.5 py-1 rounded-full bg-white/10">{battle.team_size}v{battle.team_size}</span>
        </div>

        {battle.status === "settled" && (
          <div className="rounded-2xl bg-gradient-to-r from-[#FFB42D]/15 to-[#FF2D7A]/15 border border-[#FFB42D]/30 p-4 mb-4 text-center" data-testid="battle-result">
            <div className="text-3xl mb-1">{battle.winner_side ? "🏆" : "🤝"}</div>
            <div className="font-extrabold">
              {battle.winner_side
                ? `${battle.winner_side === "a" ? battle.team_a.name : battle.team_b.name} wins the pot!`
                : "It's a tie — everyone's stake was refunded."}
            </div>
            <div className="text-sm text-white/60 mt-1">
              Combined scores: {battle.team_a.name} {battle.final_scores?.a ?? 0} — {battle.final_scores?.b ?? 0} {battle.team_b.name}
            </div>
            {iWon && <div className="text-sm font-bold text-[#FFB42D] mt-1">You earned {battle.share_per_winner} coins 🎉</div>}
          </div>
        )}

        <div className="flex gap-3 mb-4">
          <TeamColumn team={battle.team_a} size={battle.team_size} side="a"
            canJoin={canJoin} joining={busy} onJoin={join}
            rungs={battle.member_rungs} winner={battle.winner_side === "a"} />
          <TeamColumn team={battle.team_b} size={battle.team_size} side="b"
            canJoin={canJoin} joining={busy} onJoin={join}
            rungs={battle.member_rungs} winner={battle.winner_side === "b"} />
        </div>

        {canPlay && (
          <button type="button" onClick={() => navigate(`/plyx/millions?battle=${battleId}`)} data-testid="battle-play-btn"
            className="w-full py-3.5 rounded-full bg-gradient-to-r from-[#FF2D7A] via-[#FFB42D] to-[#00E0C7] text-black font-extrabold mb-3">
            ⚔️ Play your climb now
          </button>
        )}
        {battle.status === "active" && isMember && !canPlay && (
          <p className="text-center text-sm text-white/50 mb-3" data-testid="battle-waiting">
            Your climb is locked in — waiting for the other players to finish.
          </p>
        )}
        {battle.status === "open" && (
          <p className="text-center text-xs text-white/40 mb-3">
            The battle goes LIVE the moment both teams are full. Joining escrows your {battle.stake_coins}-coin stake (refunded if the battle is cancelled).
          </p>
        )}
        {battle.status === "open" && isMember && !isCreator && (
          <button type="button" onClick={() => act("leave")} disabled={busy} data-testid="battle-leave-btn"
            className="w-full py-2.5 rounded-full bg-white/10 text-sm font-bold mb-2 disabled:opacity-50">
            Leave battle (refund)
          </button>
        )}
        {battle.status === "open" && isCreator && (
          <button type="button" onClick={() => act("cancel")} disabled={busy} data-testid="battle-cancel-btn"
            className="w-full py-2.5 rounded-full bg-white/10 text-sm font-bold mb-2 disabled:opacity-50">
            Cancel battle (refund everyone)
          </button>
        )}

        <BattleChat battleId={battleId} user={user} />
      </div>
    </div>
  );
}
